/**
 * OnboardingCard - First-time contractor setup experience
 * 
 * Beautiful, non-intrusive card that guides new contractors through:
 * 1. Company profile setup
 * 2. Creating their first customer
 * 
 * Design principles:
 * - Premium, modern appearance
 * - Generous padding and spacing
 * - Clear hierarchy with progress indicator
 * - Mobile-safe
 * - Does NOT create fake/demo data
 * 
 * NOTE: This card is SUPPRESSED when first-value onboarding is active.
 * The new guided flow replaces this generic onboarding experience.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { toast } from "sonner";
import {
  Buildings,
  User,
  CheckCircle,
  Circle,
  ArrowRight,
  X,
  Sparkle,
  Briefcase,
  Receipt,
  CalendarBlank,
} from "phosphor-react";
import CompanySetupModal from "./CompanySetupModal";
import { useFirstValueOnboarding } from "../contexts/FirstValueOnboardingContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function OnboardingCard({ onStatusChange }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [showCompanySetup, setShowCompanySetup] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Check if first-value onboarding is active - suppress this card if so
  const { suppressOldOnboarding, loading: fvoLoading } = useFirstValueOnboarding();

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/onboarding/status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        
        // Check if just completed
        if (data.completed && !showSuccess) {
          setShowSuccess(true);
        }
        
        // Notify parent of status change
        if (onStatusChange) {
          onStatusChange(data);
        }
      }
    } catch (error) {
      console.error("Error fetching onboarding status:", error);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange, showSuccess]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const response = await fetch(`${API}/onboarding/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        setStatus((prev) => ({ ...prev, show_onboarding: false, dismissed: true }));
        toast.success("You can complete setup anytime from Settings");
      }
    } catch (error) {
      toast.error("Failed to dismiss");
    } finally {
      setDismissing(false);
    }
  };

  const handleCompanySetupComplete = () => {
    setShowCompanySetup(false);
    fetchStatus();
    toast.success("Company profile saved!");
  };

  const handleAddCustomer = () => {
    // Navigate to customers page with ?new=1 to auto-open create modal
    navigate("/customers?new=1");
  };

  // Don't render while loading
  if (loading || fvoLoading) return null;
  
  // CRITICAL: Don't render if first-value onboarding is active
  // The new guided flow replaces this generic onboarding
  if (suppressOldOnboarding) return null;

  // Don't render if status not available or onboarding hidden
  if (!status || !status.show_onboarding) return null;

  // Success state - show compact completion card
  if (showSuccess || status.completed) {
    return (
      <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 overflow-hidden mb-6">
        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle size={28} weight="fill" className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-emerald-900 mb-1">
                You're ready to go!
              </h2>
              <p className="text-emerald-700 text-sm mb-4">
                Your business is set up. Here's what you can do next:
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                  onClick={() => navigate("/jobs?new=1")}
                >
                  <Briefcase size={16} className="mr-1.5" />
                  Create a Job
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                  onClick={() => navigate("/estimates?new=1")}
                >
                  <Receipt size={16} className="mr-1.5" />
                  Send an Estimate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                  onClick={() => navigate("/calendar")}
                >
                  <CalendarBlank size={16} className="mr-1.5" />
                  View Calendar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const { company_profile_completed, first_customer_created, steps_completed, total_steps } = status;

  return (
    <>
      <Card className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-orange-200/60 overflow-hidden mb-6 shadow-sm">
        {/* Progress bar */}
        <div className="h-1 bg-orange-100">
          <div
            className="h-full bg-gradient-to-r from-orange-400 to-amber-500 transition-all duration-500"
            style={{ width: `${(steps_completed / total_steps) * 100}%` }}
          />
        </div>

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkle size={24} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 mb-1">
                  Let's get your business ready
                </h2>
                <p className="text-zinc-600 text-sm sm:text-base">
                  Complete these steps to start using Free Service Pro.
                </p>
              </div>
            </div>
            
            {/* Dismiss button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-zinc-600 hover:bg-orange-100/50 -mt-1 -mr-2"
              onClick={handleDismiss}
              disabled={dismissing}
              data-testid="onboarding-dismiss-btn"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <span className="text-zinc-500">Progress:</span>
            <span className="font-semibold text-orange-600">
              {steps_completed} / {total_steps}
            </span>
          </div>

          {/* Checklist */}
          <div className="space-y-3 mb-6">
            {/* Step 1: Company Profile */}
            <div
              className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                company_profile_completed
                  ? "bg-emerald-50/80 border border-emerald-200/60"
                  : "bg-white/60 border border-orange-200/40"
              }`}
            >
              {company_profile_completed ? (
                <CheckCircle size={24} weight="fill" className="text-emerald-500 flex-shrink-0" />
              ) : (
                <Circle size={24} className="text-orange-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Buildings size={18} className={company_profile_completed ? "text-emerald-600" : "text-zinc-500"} />
                  <span className={`font-medium ${company_profile_completed ? "text-emerald-700" : "text-zinc-700"}`}>
                    Company information
                  </span>
                </div>
                {company_profile_completed && (
                  <p className="text-xs text-emerald-600 mt-0.5 ml-6">Completed</p>
                )}
              </div>
            </div>

            {/* Step 2: First Customer */}
            <div
              className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                first_customer_created
                  ? "bg-emerald-50/80 border border-emerald-200/60"
                  : "bg-white/60 border border-orange-200/40"
              }`}
            >
              {first_customer_created ? (
                <CheckCircle size={24} weight="fill" className="text-emerald-500 flex-shrink-0" />
              ) : (
                <Circle size={24} className="text-orange-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <User size={18} className={first_customer_created ? "text-emerald-600" : "text-zinc-500"} />
                  <span className={`font-medium ${first_customer_created ? "text-emerald-700" : "text-zinc-700"}`}>
                    First customer
                  </span>
                </div>
                {first_customer_created && (
                  <p className="text-xs text-emerald-600 mt-0.5 ml-6">Completed</p>
                )}
              </div>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            {!company_profile_completed ? (
              <Button
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-sm flex-1 sm:flex-none h-11 text-base font-medium"
                onClick={() => setShowCompanySetup(true)}
                data-testid="onboarding-company-setup-btn"
              >
                Complete Company Setup
                <ArrowRight size={18} className="ml-2" />
              </Button>
            ) : !first_customer_created ? (
              <Button
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-sm flex-1 sm:flex-none h-11 text-base font-medium"
                onClick={handleAddCustomer}
                data-testid="onboarding-add-customer-btn"
              >
                Add Your First Customer
                <ArrowRight size={18} className="ml-2" />
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      {/* Company Setup Modal */}
      <CompanySetupModal
        isOpen={showCompanySetup}
        onClose={() => setShowCompanySetup(false)}
        onComplete={handleCompanySetupComplete}
        prefillData={{
          company_name: status.company_name,
          phone: status.phone,
          address_street: status.address_street,
          address_city: status.address_city,
          address_state: status.address_state,
          address_zip: status.address_zip,
        }}
      />
    </>
  );
}
